import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import {
  Add as AddIcon,
  Close as CloseIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Collections as GalleryIcon,
  DeleteOutline,
  StarBorder,
  Star,
} from "@mui/icons-material";
import { useTheme } from "@mui/material";

import AddVenuePhotoModal from "./AddVenuePhotoModal";
import { VenueDocument } from "../../types/venues.types";
import { tokens } from "../../../../theme";
import apiService from "../../../../service/Api";
import { toast } from "react-toastify";

// ── Types ──────────────────────────────────────────────────────────────────

type GalleryFileType = "venue_photos" | "pa_system" | "equipment" | "other";

const GALLERY_TYPES: GalleryFileType[] = [
  "venue_photos",
  "pa_system",
  "equipment",

  "other",
];

const TYPE_LABEL: Record<GalleryFileType, string> = {
  venue_photos: "Venue",
  pa_system: "PA System",
  equipment: "Equipment",

  other: "Other",
};

const TYPE_COLOR: Record<GalleryFileType, string> = {
  venue_photos: "#4A90D9",
  pa_system: "#C29363",
  equipment: "#7B68C8",
  other: "#9E9E9E",
};

interface VenueGalleryProps {
  files: VenueDocument[];
  venueId: number;
  onRefresh?: () => void;
}

// ── Helpers ────────────────────────────────────────────────────────────────

const isGalleryType = (type: string): type is GalleryFileType =>
  GALLERY_TYPES.includes(type as GalleryFileType);

// ── Component ──────────────────────────────────────────────────────────────

const VenueGallery: React.FC<VenueGalleryProps> = ({
  files,
  venueId,
  onRefresh,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [activeFilter, setActiveFilter] = useState<GalleryFileType | "all">(
    "all",
  );
  const [settingCoverId, setSettingCoverId] = useState<number | null>(null);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [localFiles, setLocalFiles] = useState<VenueDocument[]>(files);

  React.useEffect(() => {
    setLocalFiles(files);
  }, [files]);
  const galleryFiles = localFiles.filter(
    (f) =>
      f.file_type !== "floor_plan" &&
      f.file_type !== "room_photo" &&
      isGalleryType(f.file_type),
  );

  const filtered =
    activeFilter === "all"
      ? galleryFiles
      : galleryFiles.filter((f) => f.file_type === activeFilter);

  // Count per type for filter chips
  const countByType = GALLERY_TYPES.reduce<Record<string, number>>((acc, t) => {
    acc[t] = galleryFiles.filter((f) => f.file_type === t).length;
    return acc;
  }, {});

  const openLightbox = (index: number) => setLightboxIndex(index);

  const handleSetCover = async (e: React.MouseEvent, fileId: number) => {
    e.stopPropagation();
    setSettingCoverId(fileId);
    try {
      await apiService.patch(`/api/venues/document/${fileId}/`, {
        is_cover: true,
      });
      setLocalFiles((prev) =>
        prev.map((f) => ({ ...f, is_cover: f.id === fileId })),
      );
      onRefresh?.();
      toast.success("Cover photo updated");
    } catch (err) {
      console.error("Failed to set cover photo", err);
      toast.error("Failed to set cover photo. Please try again.");
    } finally {
      setSettingCoverId(null);
    }
  };

  const handleDelete = async (e: React.MouseEvent, fileId: number) => {
    e.stopPropagation();
    setDeletingId(fileId);
    try {
      await apiService.delete(`/api/venues/document/${fileId}/`);
      setLocalFiles((prev) => prev.filter((f) => f.id !== fileId));
      if (lightboxIndex !== null && filtered[lightboxIndex]?.id === fileId) {
        closeLightbox();
      }
      onRefresh?.();
      toast.success("Photo deleted successfully");
    } catch (err) {
      const error = err as Error;
      console.error("Failed to delete photo", error.message);
      toast.error("Failed to delete photo. Please try again.");
    } finally {
      setDeletingId(null);
    }
  };

  const closeLightbox = useCallback(() => setLightboxIndex(null), []);

  const goPrev = useCallback(
    () =>
      setLightboxIndex((i) =>
        i === null ? null : i > 0 ? i - 1 : filtered.length - 1,
      ),
    [filtered.length],
  );

  const goNext = useCallback(
    () =>
      setLightboxIndex((i) =>
        i === null ? null : i < filtered.length - 1 ? i + 1 : 0,
      ),
    [filtered.length],
  );

  // Keyboard nav
  useEffect(() => {
    if (lightboxIndex === null) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") goPrev();
      if (e.key === "ArrowRight") goNext();
      if (e.key === "Escape") closeLightbox();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [lightboxIndex, goPrev, goNext, closeLightbox]);

  const selectedFile = lightboxIndex !== null ? filtered[lightboxIndex] : null;

  if (galleryFiles.length === 0) {
    return (
      <Box
        sx={{
          p: 6,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 2,
        }}
      >
        <GalleryIcon
          sx={{ fontSize: 52, color: "text.disabled", opacity: 0.4 }}
        />
        <Typography variant="h6" color="text.secondary">
          No photos yet
        </Typography>
        <Typography variant="body2" color="text.disabled" sx={{ mb: 1 }}>
          Add venue photos, PA system, equipment and more
        </Typography>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={() => setAddModalOpen(true)}
          sx={{
            textTransform: "none",
            borderColor: "#C29363",
            color: "#C29363",
            "&:hover": {
              borderColor: "#a87d4f",
              bgcolor: "rgba(194,147,99,0.06)",
            },
          }}
        >
          Add Photos
        </Button>
        <AddVenuePhotoModal
          open={addModalOpen}
          onClose={() => setAddModalOpen(false)}
          onSuccess={() => {
            setAddModalOpen(false);
            onRefresh?.();
          }}
          venueId={venueId}
        />
      </Box>
    );
  }

  // ── Main render ──────────────────────────────────────────────────────────

  return (
    <Box sx={{ p: 2 }}>
      {/* ── Toolbar: filters + add button ── */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2.5,
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        {/* Filter chips */}
        <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
          <Chip
            label={`All (${galleryFiles.length})`}
            size="small"
            onClick={() => setActiveFilter("all")}
            sx={{
              fontWeight: activeFilter === "all" ? 700 : 400,
              bgcolor:
                activeFilter === "all" ? "rgba(0,0,0,0.08)" : "transparent",
              border: "1px solid",
              borderColor:
                activeFilter === "all" ? "text.secondary" : "divider",
              cursor: "pointer",
            }}
          />
          {GALLERY_TYPES.filter((t) => countByType[t] > 0).map((t) => (
            <Chip
              key={t}
              label={`${TYPE_LABEL[t]} (${countByType[t]})`}
              size="small"
              onClick={() => setActiveFilter(t)}
              sx={{
                fontWeight: activeFilter === t ? 700 : 400,
                bgcolor:
                  activeFilter === t ? `${TYPE_COLOR[t]}20` : "transparent",
                border: "1px solid",
                borderColor: activeFilter === t ? TYPE_COLOR[t] : "divider",
                color: activeFilter === t ? TYPE_COLOR[t] : "text.secondary",
                cursor: "pointer",
              }}
            />
          ))}
        </Box>

        <Button
          variant="outlined"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setAddModalOpen(true)}
          sx={{
            textTransform: "none",
            borderColor: "#C29363",
            color: "#C29363",
            "&:hover": {
              borderColor: "#a87d4f",
              bgcolor: "rgba(194,147,99,0.06)",
            },
          }}
        >
          Add Photos
        </Button>
      </Box>

      {/* ── Gallery grid ── */}
      {filtered.length === 0 ? (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ py: 4, textAlign: "center" }}
        >
          No {TYPE_LABEL[activeFilter as GalleryFileType]} photos yet.
        </Typography>
      ) : (
        <Box
          sx={{
            columns: { xs: 2, sm: 3, md: 4 },
            gap: "10px",
            "& > *": { breakInside: "avoid", mb: "10px" },
          }}
        >
          {filtered.map((f, index) => (
            <Box
              key={f.id}
              onClick={() => openLightbox(index)}
              sx={{
                position: "relative",
                borderRadius: 1.5,
                overflow: "hidden",
                cursor: "pointer",
                display: "block",
                "&:hover img": { transform: "scale(1.04)" },
                "&:hover .type-badge": { opacity: 1 },
                "&:hover .delete-btn": { opacity: 1 }, // ← add this
                "&:hover .cover-btn": { opacity: 1 },
              }}
            >
              <Box
                component="img"
                src={f.file}
                alt={`photo-${f.id}`}
                sx={{
                  width: "100%",
                  display: "block",
                  transition: "transform 0.25s ease",
                  borderRadius: 1.5,
                }}
              />

              {/* ── Delete button ── */}
              <IconButton
                className="delete-btn"
                onClick={(e) => handleDelete(e, f.id)}
                disabled={deletingId === f.id}
                size="small"
                sx={{
                  position: "absolute",
                  top: 6,
                  right: 6,
                  opacity: 0,
                  transition: "opacity 0.2s",
                  bgcolor: "rgba(0,0,0,0.55)",
                  color: "#fff",
                  padding: "4px",
                  "&:hover": { bgcolor: "rgba(211,47,47,0.85)" },
                  "&.Mui-disabled": { opacity: 0.5, color: "#fff" },
                }}
              >
                {deletingId === f.id ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <DeleteOutline sx={{ fontSize: 16 }} />
                )}
              </IconButton>

              {/* ── Set as cover button ── */}
              <IconButton
                className="cover-btn"
                onClick={(e) => handleSetCover(e, f.id)}
                disabled={settingCoverId === f.id || f.is_cover}
                size="small"
                sx={{
                  position: "absolute",
                  top: 6,
                  left: 6,
                  opacity: f.is_cover ? 1 : 0,
                  transition: "opacity 0.2s",
                  bgcolor: f.is_cover
                    ? "rgba(76,175,80,0.9)"
                    : "rgba(0,0,0,0.55)",
                  color: "#fff",
                  padding: "4px",
                  "&:hover": { bgcolor: "rgba(76,175,80,0.9)" },
                  "&.Mui-disabled": {
                    opacity: f.is_cover ? 1 : 0.5,
                    color: "#fff",
                  },
                }}
              >
                {settingCoverId === f.id ? (
                  <CircularProgress size={14} color="inherit" />
                ) : f.is_cover ? (
                  <Star sx={{ fontSize: 16 }} />
                ) : (
                  <StarBorder sx={{ fontSize: 16 }} />
                )}
              </IconButton>
              {/* type badge — unchanged */}

              <Box
                className="type-badge"
                sx={{
                  position: "absolute",
                  bottom: 6,
                  left: 6,
                  opacity: 0,
                  transition: "opacity 0.2s",
                  bgcolor: `${TYPE_COLOR[f.file_type as GalleryFileType] ?? "#9E9E9E"}CC`,
                  borderRadius: 0.75,
                  px: 0.75,
                  py: 0.2,
                }}
              >
                <Typography
                  sx={{ fontSize: "0.6rem", color: "#fff", fontWeight: 700 }}
                >
                  {TYPE_LABEL[f.file_type as GalleryFileType] ?? f.file_type}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {/* ── Lightbox ── */}
      <Dialog
        open={lightboxIndex !== null}
        onClose={closeLightbox}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: colors.primary[900],
            borderRadius: 2,
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            py: 1.5,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {selectedFile
                ? (TYPE_LABEL[selectedFile.file_type as GalleryFileType] ??
                  "Photo")
                : "Photo"}
            </Typography>
            {lightboxIndex !== null && (
              <Typography variant="caption" color="text.secondary">
                {lightboxIndex + 1} / {filtered.length}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {selectedFile && (
              <Button
                size="small"
                startIcon={selectedFile.is_cover ? <Star /> : <StarBorder />}
                disabled={
                  selectedFile.is_cover || settingCoverId === selectedFile.id
                }
                onClick={(e) => handleSetCover(e, selectedFile.id)}
                sx={{ textTransform: "none" }}
              >
                {selectedFile.is_cover ? "Cover Photo" : "Set as Cover"}
              </Button>
            )}
            <IconButton onClick={closeLightbox} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent
          sx={{ p: 0, position: "relative", bgcolor: "rgba(0,0,0,0.04)" }}
        >
          {selectedFile && (
            <Box
              sx={{
                position: "relative",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: 300,
              }}
            >
              {filtered.length > 1 && (
                <IconButton
                  onClick={goPrev}
                  sx={{
                    position: "absolute",
                    left: 12,
                    zIndex: 2,
                    bgcolor: colors.primary[900],
                    boxShadow: 2,
                    "&:hover": { bgcolor: colors.primary[800] },
                  }}
                >
                  <ChevronLeftIcon />
                </IconButton>
              )}

              <Box
                component="img"
                src={selectedFile.file}
                alt={`photo-${selectedFile.id}`}
                sx={{
                  width: "100%",
                  maxHeight: "70vh",
                  objectFit: "contain",
                  display: "block",
                }}
              />

              {filtered.length > 1 && (
                <IconButton
                  onClick={goNext}
                  sx={{
                    position: "absolute",
                    right: 12,
                    zIndex: 2,
                    bgcolor: colors.primary[900],
                    boxShadow: 2,
                    "&:hover": { bgcolor: colors.primary[800] },
                  }}
                >
                  <ChevronRightIcon />
                </IconButton>
              )}
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Upload modal ── */}
      <AddVenuePhotoModal
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onSuccess={() => {
          setAddModalOpen(false);
          onRefresh?.();
        }}
        venueId={venueId}
      />
    </Box>
  );
};

export default VenueGallery;
